/**
 * The canteen helpers the home screen still uses. The ordering screen is the week planner now
 * (utils/canteenPlanner.ts); the old per-day cart went with it (22 Oct 2026).
 */

import type { CanteenOrder } from '../types/wallet';
import { isoDate as isoDay } from './canteenPlanner';

export { isoDay };

const statusKey = (o: CanteenOrder) => String(o.orderStatus ?? '').toUpperCase();
const FINISHED = ['DELIVERED', 'COLLECTED', 'COMPLETED'];

/**
 * The child's orders served today that are not cancelled. Orders placed ahead carry
 * serveDate; older payloads fall back to the order date.
 */
export function ordersServedToday(orders: CanteenOrder[], today: Date): CanteenOrder[] {
  const day = isoDay(today);
  const served = (o: CanteenOrder) => o.serveDate ?? isoDay(new Date(o.orderDate));
  return orders.filter(o => served(o) === day && !['CANCELLED', 'REJECTED'].includes(statusKey(o)));
}

/**
 * The child's order for today on the home screen. One still open (placed, being prepared,
 * ready) wins over one already collected, then the newest: a collected breakfast must not
 * stand in for the order just placed, which read as "Collected" (QA, 31 Oct 2026). An order
 * placed ahead is stored at the start of its day, so OrderDate alone put it behind an older
 * order placed that morning.
 */
export function todaysOrder(orders: CanteenOrder[], today: Date): CanteenOrder | null {
  const open = (o: CanteenOrder) => (FINISHED.includes(statusKey(o)) ? 0 : 1);
  return ordersServedToday(orders, today)
    .sort((a, b) => open(b) - open(a)
      || b.orderDate.localeCompare(a.orderDate)
      || b.orderNumber.localeCompare(a.orderNumber, undefined, { numeric: true }))[0] ?? null;
}
